/* ====== Add-On Flyout ***
 * Reads product data from window.__flyoutProducts (server-rendered JSON).
 * No DOM scraping, no data-attribute parsing at runtime.
 * Price display delegates entirely to window.initDynamicPrices() from global.js,
 * which reads data-compare / data-regular / data-sale / data-vip / data-vvip
 * attributes and toggles the correct price element visible. ======= */

(function () {
  'use strict';

  /* ======= State ======= */
  let selectedProducts       = [];
  let checkedProducts        = [];
  let variantPriceMap        = [];
  let prevEngravingMaxLength = 0;

  /* ======= Config / data injected by Liquid ======= */
  const PRODUCTS = window.__flyoutProducts || [];
  const CFG      = window.__flyoutConfig   || {};
  const {
    isSplit         = false,
    splitCategories = [],
    categoryLabels  = {},
    salePriceColor  = globalSalePriceColor,
  } = CFG;

  /* ======= DOM refs ======= */
  const drawer       = document.querySelector('.custom-drawer');
  const productsList = document.getElementById('flyoutProductsList');
  const confirmBtn   = document.getElementById('grab-selected-products');
  const listWrap     = document.querySelector('.flyout-list-wrap');
  const calcPriceEl  = document.getElementById('calculatePrice');
  const addBtn       = document.getElementById('add-button');

  if (!drawer || !productsList) return;

  /* ======= Build drawer product list ======= */
  function buildDrawerList() {
    productsList.innerHTML = '';

    const renderItem = (item) => {
      const li = document.createElement('li');
      li.dataset.productId = item.productId;

      const isChecked = selectedProducts.some(s => s.sourceId === item.id);
      const checkId   = `prod-${item.id}`;

      li.innerHTML = `
        <div class="checkbox-wrap">
          <div class="form-group">
            <input type="checkbox" class="select-product" id="${checkId}"
              data-id="${item.id}"
              data-pId="${item.productId}"
              ${isChecked ? 'checked' : ''} />
            <label for="${checkId}"></label>
          </div>
        </div>
        <div class="products-list-img">
          <img src="${item.image}" alt="${item.title}" loading="lazy" />
        </div>
        <div class="products-list-details">
          <a href="${item.url}"><h3>${item.title}</h3></a>
          ${buildPriceHTML(item)}
        </div>`;

      li.querySelector('.products-list-img').addEventListener('click', () => {
        li.querySelector('.select-product').click();
      });
      li.querySelector('.select-product').addEventListener('change', onCheckboxChange);
      return li;
    };

    if (isSplit) {
      splitCategories.forEach(cat => {
        const catItems = PRODUCTS.filter(p => p.productType === cat);
        if (!catItems.length) return;

        const heading = document.createElement('h4');
        heading.className   = 'sidebar-drawer-sticky';
        heading.textContent = categoryLabels[cat] || cat;
        productsList.appendChild(heading);
        catItems.forEach(item => productsList.appendChild(renderItem(item)));
      });
    } else {
      PRODUCTS.forEach(item => productsList.appendChild(renderItem(item)));
    }
    // Back-to-top
    const backBtn = document.createElement('a');
    backBtn.href        = '#';
    backBtn.className   = 'back-to-top';
    backBtn.textContent = 'Back to Top';
    backBtn.addEventListener('click', e => {
      e.preventDefault();
      productsList.scrollTo({ top: 0, behavior: 'smooth' });
    });
    productsList.appendChild(backBtn);

    // Run initDynamicPrices so the newly rendered price elements get
    runDynamicPrices();
  }

  /* ======= Price HTML builder ======= */
  function buildPriceHTML(item, overrideVariantPrice) {
    const uid = item.id;
    const regularCents = overrideVariantPrice !== undefined
      ? overrideVariantPrice
      : (item.hasUniquePrices && item.minPrice ? item.minPrice : item.price);
    const regular = parseFloat((regularCents / 100).toFixed(2));
    const compareAt = parseFloat(((item.compareAtPrice || 0) / 100).toFixed(2));
    const sale = parseFloat(item.sale?.salePrice || 0);
    const vip = parseFloat(item.sale?.vipPrice || 0);
    const vvip = parseFloat(item.sale?.vvipPrice || 0);
    const fmt = (n) => {
      const num = parseFloat(n) || 0;
      const hasCents = Math.round(num * 100) % 100 !== 0;
      return `$${num.toLocaleString('en-US', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2
      })}`;
    };

    const hasUniquePrices = item.hasUniquePrices === true;

    return `
      <div class="price flyout-item-price"
        data-compare="${compareAt}"
        data-regular="${regular}"
        data-sale="${sale}"
        data-vip="${vip}"
        data-vvip="${vvip}"
        data-id="${uid}">
        <div class="price-container">
          ${hasUniquePrices ? `<span class="rc-starting-at">${textBeforePrice}</span>` : ''}
          ${
            compareAt > regular
              ? `
                <span class="compare-at-price-${uid}">
                  <span class="visually-hidden">Sale price</span>
                  <span class="visually-hidden">Regular price</span>
                  <s class="price-item price-item--regular" dir="ltr">
                    <span class="money">${fmt(compareAt)}</span>
                  </s>
                  <span class="rc-main-price">
                    <span class="money">${fmt(regular)}</span>
                  </span>
                </span>`
              : ''
          }

          <span class="regular-price-${uid}" dir="ltr">
            <span class="visually-hidden">Regular price</span>
            <span class="price-item price-item--regular">
              <span class="money">${fmt(regular)}</span>
            </span>
          </span>
          <span class="rc-sale-price-${uid} is-hide">
            <s>
              <span class="money">${fmt(regular)}</span>
            </s>
            ${
              sale && sale < regular
                ? `
                  <span class="price-discount">
                    ${Math.round(((regular - sale) / regular) * 100)}% Off
                  </span>`
                : ''
            }
            <span class="rc-sale-price">
              <span class="money">${fmt(sale)}</span>
            </span>
          </span>
          <span class="rc-vip-price-${uid} is-hide">
            <s>
              <span class="money">${fmt(regular)}</span>
            </s>
            ${
              vip && vip < regular
                ? `
                  <span class="price-discount">
                    ${Math.round(((regular - vip) / regular) * 100)}% Off
                  </span>`
                : ''
            }
            <span class="rc-sale-price">
              <span class="money">${fmt(vip)}</span>
            </span>
          </span>
          <span class="rc-vvip-price-${uid} is-hide">
            <s>
              <span class="money">${fmt(regular)}</span>
            </s>
            ${
              vvip && vvip < regular
                ? `
                  <span class="price-discount">
                    ${Math.round(((regular - vvip) / regular) * 100)}% Off
                  </span>
                `
                : ''
            }
            <span class="rc-sale-price">
              <span class="money">${fmt(vvip)}</span>
            </span>
          </span>
        </div>
      </div>
    `;
  }

  /* ======= Calling initDynamicPrices() from global.js.
   * That function uses $(".price").each() so it will pick up ALL .price elements
   * on the page, including the flyout ones we just rendered. ======= */
  function runDynamicPrices() {
    if (typeof window.initDynamicPrices === 'function') {
      window.initDynamicPrices();
    }
  }

  /* ======= Checkbox change ======= */
  function onCheckboxChange(e) {
    const cb   = e.target;
    const id   = parseInt(cb.dataset.id, 10);
    const item = PRODUCTS.find(p => p.id === id);
    if (!item) return;
    if (cb.checked) {
      if (!checkedProducts.some(p => p.sourceId === id)) {
        checkedProducts.push(mapToSelection(item));
      }
    } else {
      checkedProducts = checkedProducts.filter(p => p.sourceId !== id);
    }
    updateConfirmBtnText();
  }

  function mapToSelection(item) {
    return {
      sourceId:      item.id,
      id:            item.id,
      pId:           item.productId,
      price:         item.price,
      title:         item.title,
      handle:        item.handle,
      desc:          item.variantTitle,
      image:         item.image,
      url:           item.url,
      leadtime:      item.leadtime,
      finalSale:     item.finalSale,
      engraving:     item.engraving,
      salePrice:     item.sale.salePrice,
      vipPrice:      item.sale.vipPrice,
      vvipPrice:     item.sale.vvipPrice,
      compareAtPrice:item.compareAtPrice,
      metaFields:    item.metaFields,
      sale:          item.sale,
    };
  }

  function updateConfirmBtnText() {
    confirmBtn.textContent = checkedProducts.length ? `SELECTED ${checkedProducts.length} ITEMS` : 'SELECTED ITEMS';
  }

  /* ======= Open Drawer ======= */
  function openDrawer() {
    productsList.querySelectorAll('.select-product').forEach(cb => {
      const id = parseInt(cb.dataset.id, 10);
      cb.checked = selectedProducts.some(s => s.sourceId === id);
    });
    checkedProducts = [...selectedProducts];
    updateConfirmBtnText();

    drawer.classList.add('show-custom-drawer');
    document.body.classList.add('hidescroll');
    document.body.classList.remove('is-rc-close');
  }

  /* ======= Close Drawer ======= */
  function closeDrawer() {
    productsList.scrollTo({ top: 0, behavior: 'smooth' });
    drawer.classList.remove('show-custom-drawer');
    document.body.classList.remove('hidescroll');
  }

  addBtn?.addEventListener('click', openDrawer);
  document.querySelectorAll('.js-close-drawer').forEach(el => el.addEventListener('click', closeDrawer));

  /* ======= Confirm selection ======= */
  confirmBtn.addEventListener('click', () => {
    // Everything is re-rendered below, so reset variant data per selected card (sourceId is unique per card)
    const newKeys   = checkedProducts.map(p => p.sourceId);
    variantPriceMap = variantPriceMap.filter(v => !newKeys.includes(v.sourceId));

    selectedProducts = [...checkedProducts];
    renderSelectedProducts();
    addBtn.textContent = selectedProducts.length ? 'EDIT' : 'ADD';
    closeDrawer();
  });

  /* ======= Render selected products in PDP card ======= */
  function renderSelectedProducts() {
    listWrap.innerHTML = '';
    if (!selectedProducts.length) {
      listWrap.classList.add('is-hide');
      setTimeout(() => { if (calcPriceEl) calcPriceEl.textContent = ''; }, 500);
      return;
    }
    listWrap.classList.remove('is-hide');

    selectedProducts.forEach(product => {
      product.leadtime = '';

      // sourceId is unique per selected card, even when two variants of the same product are selected
      const key = product.sourceId;

      const el = product.engraving?.enabled
        ? buildEngravingCard(product)
        : buildProductCard(product);
      listWrap.appendChild(el);

      // Clear engraving errors as user fixes them
      el.querySelector(`#selected-font-${key}`)
        ?.addEventListener('change', () => {
          const err = el.querySelector('.font-selector-flyout-error');
          if (err) { err.textContent = ''; err.classList.add('is-hide'); }
        });

      el.querySelector(`#selected-placement-${key}`)
        ?.addEventListener('change', () => {
          const err = el.querySelector('.placement-selector-flyout-error');
          if (err) { err.textContent = ''; err.classList.add('is-hide'); }
        });

      el.querySelector(`.flyout-engraving-text-one-${key}`)
        ?.addEventListener('input', () => {
          const err = el.querySelector('.custom-text-one-flyout-error');
          if (err) { err.textContent = ''; err.classList.add('is-hide'); }
        });

      fetchVariantDropdowns(product.handle, key, product.metaFields);
    });

    setCalculatedPrice();
    attachRemoveHandlers();

    // Re-run initDynamicPrices so the newly rendered PDP card price elements
    runDynamicPrices();
  }

  /* ======= Product card builders ======= */
  function buildProductCard(product) {
    const key = product.sourceId;
    const div = document.createElement('div');
    div.className = 'flyout-list';
    div.innerHTML = `
      <div class="flyout-img">
        <img src="${product.image}" alt="${product.title}" loading="lazy" />
      </div>
      <div class="flyout-list-details">
        <div class="title-height-stack-with">
          <a href="${product.url}"><h4>${product.title}</h4></a>
          <a href="javascript:void(0)" class="remove-product"
            data-source-id="${key}"
            data-var-product-id="${product.pId}" data-var-id="${product.id}">
            <svg class="icon icon-close" width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg"><path d="M21 1L1 21M1 1L21 21" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"></path></svg>
          </a>
          <a href="javascript:void(0)" class="remove-product-disabled is-hide">
            <svg class="icon icon-close" width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg"><path d="M21 1L1 21M1 1L21 21" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"></path></svg>
          </a>
        </div>
        <div id="insert-dropdown-${key}"></div>
        <p class="grab-product-leadtime-${key} stack-with-lead-time show-lead-time-add-on-flyout is-hide">${product.leadtime || ''}</p>
      </div>`;
    return div;
  }

  /* ======= Render Engraving Options ======= */
  function buildEngravingCard(product) {
    const eng   = product.engraving;
    const fonts = (eng.fonts || []).filter(Boolean);
    const key   = product.sourceId;
    const fontOptions = fonts.map(f => {
      const family = f === 'Bernhard Tango' ? 'BernhardTangoBT' : f === 'Celine' ? 'BV Celine' : f.replace(/\s/g, '');
      return `<option value="${f}" style="font-family:${family}${f === 'Celine' ? ';text-transform:uppercase' : ''}">${f}</option>`;
    }).join('');
    let placementHTML = '';

    if (eng.placementOpts || eng.styleOpts || eng.placement) {
      const placementOptions = (eng.placementOptions || []).filter(Boolean);
      const optionsHTML = placementOptions
        .map(o => `<option value="${o}">${o}</option>`)
        .join('');

      placementHTML = `
        <label class="label-text">Engraving Placement</label>
        <div class="dropdown-container">
          <select id="selected-placement-${key}" disabled
            class="dropdown custom-engraving-selector handle-disabled-dropdown"
            onchange="window.__flyout.setMaxLength(${!!eng.styleOpts})">
            <option value="">Select</option>
            ${optionsHTML}
          </select>
          <div class="errormsg rc-custom-error-message is-hide placement-selector-flyout-error"></div>
        </div>`;
    }

    const charLen  = eng.customLength || '';
    const maxAttr  = charLen ? `maxlength="${charLen}"` : 'maxlength="0"';
    const countOne = charLen ? `0/${charLen}` : '0/0';
    const onKeyUp  = charLen
      ? `window.__flyout.countFont(${key},${charLen})`
      : `window.__flyout.countEngraving(${key})`;

    const div = document.createElement('div');
    div.className = 'flyout-list';
    div.innerHTML = `
      <div class="flyout-img">
        <img src="${product.image}" alt="${product.title}" loading="lazy" />
      </div>
      <div class="flyout-list-details">
        <div class="title-height">
          <a href="${product.url}"><h4>${product.title}</h4></a>
          <a href="javascript:void(0)" class="remove-product"
            data-source-id="${key}"
            data-var-product-id="${product.pId}" data-var-id="${product.id}">
            <svg class="icon icon-close" width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg"><path d="M21 1L1 21M1 1L21 21" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"></path></svg>
          </a>
          <a href="javascript:void(0)" class="remove-product-disabled is-hide">
            <svg class="icon icon-close" width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg"><path d="M21 1L1 21M1 1L21 21" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"></path></svg>
          </a>
        </div>
        <div class="engraving-product">
          <div class="customflex">
            <div class="Label-details">
              <h4>(Engraving <span class="money">$<span class="engravingPrice"></span></span>)</h4>
              <p>Please add an additional 2–3 weeks for engraving.</p>
            </div>
            <label class="engraving-toggle-switch" tabindex="0">
              <input type="checkbox" class="engraving-toggle-checkbox handle-disabled-dropdown"
                id="engravablesInputsWrapperMejuri${key}"
                onchange="window.__flyout.toggleEngraving(${key})" />
              <span class="engraving-toggle-track">
                <span class="engraving-toggle-thumb"></span>
              </span>
            </label>
          </div>
          <div id="insert-dropdown-${key}"></div>

          <div class="rc-engravable-option-selector">
            <label class="label-text">Font</label>
            <div class="dropdown-container">
              <select id="selected-font-${key}" disabled
                class="dropdown custom-font-selector handle-disabled-dropdown"
                onchange="window.__flyout.setupFontPreview()">
                <option value="">Select</option>
                ${fontOptions}
              </select>
              <div class="errormsg rc-custom-error-message is-hide font-selector-flyout-error"></div>
            </div>

            ${placementHTML}

            <div class="custom-input-boxes">
              <input type="text" disabled ${maxAttr}
                id="mejuriEngravingOne"
                class="textbox customization-text-box handle-disabled-dropdown flyout-engraving-text-one-${key}"
                placeholder="Enter your text here"
                onkeyup="${onKeyUp}" onkeypress="${onKeyUp}" />
              <span id="character_count_display_one">${countOne}</span>
              <p id="fontPreviewMejuriOne" class="fontPreviewMejuri"></p>
              <div class="errormsg is-hide rc-custom-error-message custom-text-one-flyout-error"></div>
              <input type="text" disabled id="mejuriEngravingTwo"
                class="textbox is-hide customization-text-box handle-disabled-dropdown flyout-engraving-text-two-${key}"
                placeholder="Enter second line text here" />
              <span class="is-hide" id="character_count_display_two">0/0</span>
              <p id="fontPreviewMejuriTwo" class="fontPreviewMejuri"></p>
              <div class="errormsg is-hide custom-text-two-flyout-error"></div>
            </div>
          </div>
        </div>

        <p class="grab-product-leadtime-${key} stack-with-lead-time show-lead-time-add-on-flyout is-hide">${product.leadtime || ''}</p>
      </div>`;

    const priceSpan = div.querySelector('.engravingPrice');
    if (typeof engravingProductPrice !== 'undefined') {
      priceSpan.textContent = engravingProductPrice;
    }

    return div;
  }

  /* ======= Remove handlers ======= */
  function attachRemoveHandlers() {
    listWrap.querySelectorAll('.remove-product').forEach(btn => {
      btn.addEventListener('click', function () {
        const srcId = parseInt(this.dataset.sourceId, 10);
        selectedProducts = selectedProducts.filter(p => p.sourceId !== srcId);
        variantPriceMap  = variantPriceMap.filter(v => v.sourceId !== srcId);
        document.querySelector('.ProductForm__AddToCart')?.setAttribute('data-action', 'add-to-cart-with-wishlist');
        renderSelectedProducts();
        /* ======= Manage Flyout CTA text ======= */
        addBtn.textContent = selectedProducts.length ? 'EDIT' : 'ADD';
      });
    });
  }

  /* ======= Fetch variant dropdowns =======
   * `key` is the unique selected-card key (sourceId), NOT the Shopify product id,
   * so two variants of the same product each get their own container and select ids. */
  function fetchVariantDropdowns(handle, key, metaFields) {
    fetch(`/products/${handle}.js`)
      .then(r => r.json())
      .then(product => {
        const container = document.getElementById(`insert-dropdown-${key}`);
        if (!container) return;

        let html = '';
        product.options.forEach((opt, idx) => {
          const values = [...new Set(product.variants.map(v => v[`option${idx + 1}`]))];
          html += `
            <div class="product-option">
              <label class="label-text" for="opt-${idx + 1}-${key}">${opt.name}:</label>
              <div class="dropdown-container">
                <select class="dropdown custom-dropdown-stack-with customization-text-box-stack-with handle-disabled-dropdown"
                  id="opt-${idx + 1}-${key}" name="option${idx + 1}" data-required="true">
                  <option value="">Select</option>
                  ${values.map(v => `<option value="${v}">${v}</option>`).join('')}
                </select>
                <span class="validation-message rc-custom-error-message validation-add-on-products"
                  id="validation-opt-${idx + 1}-${key}"
                  style="color:#b00020;display:none">This field is required.</span>
              </div>
            </div>`;
        });
        container.innerHTML = html;

        product.options.forEach((_, idx) => {
          const sel = document.getElementById(`opt-${idx + 1}-${key}`);
          sel?.addEventListener('change', () => {
            // Clear inline-style validation span on fix
            const errSpan = document.getElementById(`validation-opt-${idx + 1}-${key}`);
            if (errSpan && sel.value) errSpan.style.display = 'none';
            onVariantChange(product, metaFields, key);
          });
        });

        document.querySelector('.ProductForm__AddToCart')?.addEventListener('click', () => {
          validateDropdowns(product, key);
        });
      })
      .catch(() => {});
  }

  /* ======= Variant Change Function: Run for Selected products ======= */
  function onVariantChange(product, metaFields, key) {
    const selected = {};
    product.options.forEach((_, idx) => {
      selected[`option${idx + 1}`] = document.getElementById(`opt-${idx + 1}-${key}`)?.value || '';
    });

    const variant = findVariant(product.variants, selected);
    if (!variant) return;

    const entry = variantPriceMap.find(v => v.sourceId === key);
    if (entry) {
      Object.assign(entry, { variantPrice: variant.price, metaFields, variant });
    } else {
      variantPriceMap.push({ sourceId: key, productId: product.id, variantPrice: variant.price, metaFields, variant });
    }

    document.querySelector(`.remove-product[data-source-id="${key}"]`)?.setAttribute('data-var-id', variant.id);

    // Update selectedProducts entry with variant-level prices from metaFields
    const sp = selectedProducts.find(p => p.sourceId === key);
    if (sp) {
      const mf = sp.metaFields?.[variant.id];
      sp.id       = variant.id;
      sp.price    = variant.price;
      // Update sale object so buildPriceHTML picks up the right variant prices
      sp.sale = {
        salePrice: mf?.custom_sale_price_currency?.amount || sp.salePrice || 0,
        vipPrice:  mf?.vip_price_sale_currency?.amount    || sp.vipPrice  || 0,
        vvipPrice: mf?.vvip_price_sale_currency?.amount   || sp.vvipPrice || 0,
      };
      sp.compareAtPrice = variant.compare_at_price || sp.compareAtPrice || 0;

      // Re-render the price block for this specific item with the variant's prices, then let initDynamicPrices pick the correct visible tier
      const priceEl = listWrap.querySelector(`.flyout-item-price[data-id="${variant.id}"], .flyout-item-price[data-id="${sp.id}"]`)
                   || listWrap.querySelectorAll('.flyout-item-price')[selectedProducts.findIndex(p => p.sourceId === key)];
      if (priceEl) {
        priceEl.outerHTML = buildPriceHTML(sp, variant.price);
      }

      runDynamicPrices();
    }

    setCalculatedPrice();

    const allOptionsSelected = product.options.every((_, idx) =>
      !!document.getElementById(`opt-${idx + 1}-${key}`)?.value
    );
    if (allOptionsSelected) {
      document.querySelector(`.grab-product-leadtime-${key}`)?.classList.remove('is-hide');
      fetchLeadTime(variant, product.id, metaFields, key);
    }
  }

  function findVariant(variants, selected) {
    return variants.find(v =>
      Object.keys(selected).every(k => {
        const vVal = (v[k] || '').replace(/"/g, '').replace('\\', '');
        const sVal = (selected[k] || '').replace(/"/g, '').replace('\\', '');
        return vVal === sVal;
      })
    );
  }

  function validateDropdowns(product, key) {
    if (product.tags?.includes('Engravable')) return;
    const selected = {};
    product.options.forEach((_, idx) => {
      selected[`option${idx + 1}`] = document.getElementById(`opt-${idx + 1}-${key}`)?.value || '';
    });
    const variant = findVariant(product.variants, selected);
    if (variant) {
      pushUniqueVariant(variant.id, product.id,
        document.querySelector(`.grab-product-leadtime-${key}`)?.textContent || '');
    }
  }

  /* ======= Unique variant IDs for cart ======= */
  const selectedStackVariantIds = [];
  function pushUniqueVariant(variantId, productId, leadTime) {
    if (leadTime === 'Sold Out') return;
    if (!selectedStackVariantIds.find(v => v.id === variantId)) {
      selectedStackVariantIds.push({ id: variantId, productid: productId, leadtime: leadTime });
    }
  }

  /* ======= Total price in PDP card header ======= */
  /**
   * setCalculatedPrice computes the running total shown next to the section
   * title (the (+$X) display in #calculatePrice).
   *
   * Individual item prices in the list are handled by initDynamicPrices()
   * (which reads data-* attributes and shows the right tier span).
   */
  function setCalculatedPrice() {
    if (!calcPriceEl) return;
    if (!selectedProducts.length) {
      setTimeout(() => { calcPriceEl.innerHTML = ''; }, 500);
      return;
    }

    const visitingCustomer = localStorage.getItem('setVisitingCustomer');
    const showSale  = window.showSalePriceGlobal  === true;
    const showVIP   = window.showVIPPriceGlobal   === true && visitingCustomer === window.getVIPParam;
    const showVVIP  = window.showVVIPPriceGlobal  === true && visitingCustomer === window.getVVIPParam;
    const saleCode   = localStorage.getItem('sale_type') || '';
    const vipCode    = window.getVIPParam  || '';
    const vvipCode   = window.getVVIPParam || '';
    let totalCents      = 0;
    let totalSaleDollar = 0;
    let compareAtCents  = 0;

    // Products that have had a variant selected already have updated sp.price / sp.sale
    const getEffectivePrice = (sp) => {
      const regular    = sp.price || 0;
      const compareAt  = sp.compareAtPrice || 0;
      const sale  = parseFloat(sp.sale?.salePrice  || sp.salePrice  || 0);
      const vip   = parseFloat(sp.sale?.vipPrice   || sp.vipPrice   || 0);
      const vvip  = parseFloat(sp.sale?.vvipPrice  || sp.vvipPrice  || 0);

      totalCents     += regular;
      compareAtCents += compareAt;

      if (compareAt > regular) {
      } else if (sale && showSale) {
        totalSaleDollar += sale;
      } else if (vip && showVIP && saleCode === vipCode) {
        totalSaleDollar += vip;
      } else if (vvip && showVVIP && saleCode === vvipCode) {
        totalSaleDollar += vvip;
      }
    };

    selectedProducts.forEach(getEffectivePrice);

    // Add engraving ghost product price to total if toggled on
    const engravingChecked = document.querySelectorAll('[id^="engravablesInputsWrapperMejuri"]:checked').length > 0;
    const engravingPrice   = engravingProductPrice || 0; // dollars
    if (engravingChecked) {
      totalCents      += engravingPrice * 100;
      compareAtCents  += engravingPrice * 100;
    }

    const dollars     = totalCents / 100;
    const compareDollars = compareAtCents / 100;

    if (dollars === 0) { calcPriceEl.textContent = ''; return; }

    const fmt = (n) => n.toLocaleString('en-US', {
      minimumFractionDigits: n % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });

    const sc = salePriceColor;

    // compareAt takes priority in display (strikethrough + sale colour)
    if (compareDollars > dollars) {
      const pct = ((compareDollars - dollars) / compareDollars * 100).toFixed(2).replace(/\.00$/, '');
      calcPriceEl.innerHTML = ` (+$<span class="comma-price strikeThrough">${fmt(compareDollars)}</span> <span class="sale-price" style="color:${sc}">+$${fmt(dollars)}</span>)`;
      // calcPriceEl.innerHTML = ` (+$<span class="comma-price strikeThrough">${fmt(compareDollars)}</span> <span class="sale-price" style="color:${sc}">+$${fmt(dollars)}</span> <span class="percent-off" style="color:${sc}">${pct}% off</span>)`;
    } else if (totalSaleDollar > 0 && totalSaleDollar < dollars) {
      const pct = ((dollars - totalSaleDollar) / dollars * 100).toFixed(2).replace(/\.00$/, '');
      calcPriceEl.innerHTML = ` (+$<span class="comma-price strikeThrough">${fmt(dollars)}</span> <span class="sale-price" style="color:${sc}">$${fmt(totalSaleDollar)}</span> <span class="percent-off" style="color:${sc}">${pct}% off</span>)`;
    } else {
      calcPriceEl.textContent = ` (+$${fmt(dollars)})`;
    }
  }

  /* ======= Lead time fetch ======= */
  async function fetchLeadTime(variant, productId, metaFields, key) {
    const variantId       = variant.id;
    const inventoryPolicy = variant.inventory_policy;
    const metaFieldData   = metaFields?.[variantId];
    const customLeadTime  = metaFieldData?.custom_lead_time || '';

    if (!portalApiUrl) {
      console.warn('[flyout] portalApiUrl not set — skipping lead time fetch');
      return;
    }
    try {
      const res  = await fetch(
        `${portalApiUrl}/mongo/leadtimes?shopifyProductId=${productId}&variantId=${variantId}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      const data = await res.json();
      const { availableToSell } = data.data;

      let leadTime = '';
      if (availableToSell !== undefined && availableToSell <= 0) {
        leadTime = inventoryPolicy === 'continue' ? (customLeadTime || '') : 'Sold Out';
      } else if (availableToSell > 30) {
        leadTime = 'In Stock';
      } else if (availableToSell > 0) {
        leadTime = 'Only a few left!';
      }

      const leadEl = document.querySelector(`.grab-product-leadtime-${key}`);
      if (leadEl) {
        leadEl.textContent = leadTime;
        leadEl.classList.toggle('is-hide', !leadTime);
      }

      const sp = selectedProducts.find(p => p.sourceId === key);
      if (sp) {
        sp.leadtime = leadTime;

        const isSoldOut = leadTime === 'Sold Out';
        const engToggle = leadEl?.closest('.flyout-list')?.querySelector('[id^="engravablesInputsWrapperMejuri"]');

        if (engToggle) {
          engToggle.disabled = isSoldOut;
          const engLabel = engToggle.closest('.engraving-toggle-switch');
          if (engLabel) {
            engLabel.style.opacity       = isSoldOut ? '0.4' : '';
            engLabel.style.pointerEvents = isSoldOut ? 'none' : '';
            engLabel.style.cursor        = isSoldOut ? 'not-allowed' : '';
          }

          // Add/remove sold-out class on engraving options
          const engravableOptions = engToggle.closest('.flyout-list')?.querySelector('.rc-engravable-option-selector');
          if (engravableOptions) {
            engravableOptions.classList.toggle('rc-engravable-sold-item', isSoldOut);
          }

          if (isSoldOut && engToggle.checked) {
            engToggle.checked = false;
            const toggleVarId = parseInt(engToggle.id.replace('engravablesInputsWrapperMejuri', ''), 10);
            toggleEngraving(toggleVarId);
          }

          const engSection = engToggle.closest('.engraving-product');
          if (engSection) {
            engSection.style.opacity       = isSoldOut ? '' : '';
            engSection.style.pointerEvents = isSoldOut ? '' : '';
          }
        }
      }
    } catch (e) {
      console.error('Lead time fetch error:', e);
    }
  }

  /* ======= Engraving toggle ======= */
  function toggleEngraving(variantId) {
    const toggle  = document.getElementById(`engravablesInputsWrapperMejuri${variantId}`);
    const enabled = toggle?.checked;
    const inputs  = document.querySelectorAll('.custom-font-selector, .custom-engraving-selector, .customization-text-box');

    inputs.forEach(el => el.disabled = !enabled);

    if (enabled) {
      document.querySelectorAll('#mejuriEngravingOne, .custom-font-selector, .custom-engraving-selector')
        .forEach(el => el.setAttribute('data-required', 'true'));
      if (prevEngravingMaxLength) {
        document.getElementById('mejuriEngravingOne')?.setAttribute('maxlength', prevEngravingMaxLength);
      }
    } else {
      inputs.forEach(el => { el.value = ''; });
      prevEngravingMaxLength = document.getElementById('mejuriEngravingOne')?.getAttribute('maxlength') || 0;
      document.getElementById('mejuriEngravingOne')?.setAttribute('maxlength', '0');
      document.getElementById('character_count_display_one').textContent = '0/0';
      document.getElementById('fontPreviewMejuriOne').textContent  = '';
      document.getElementById('fontPreviewMejuriTwo').textContent  = '';
      const engTwo = document.getElementById('mejuriEngravingTwo');
      engTwo?.classList.add('is-hide');
      engTwo?.setAttribute('disabled', true);
      document.getElementById('character_count_display_two')?.classList.add('is-hide');
      document.querySelectorAll('.errormsg').forEach(el => el.classList.add('is-hide'));
      document.querySelectorAll('#mejuriEngravingOne, #mejuriEngravingTwo, .custom-font-selector, .custom-engraving-selector')
        .forEach(el => el.removeAttribute('data-required'));
    }
    setCalculatedPrice();
  }

  /* ======= Engraving placement / max-length ======= */
  function setMaxLength(isEngravingStyle) {
    const val = document.querySelector('.custom-engraving-selector') ?.value?.trim().toLowerCase().replace(/\s/g, '') || '';
    const engOne = document.getElementById('mejuriEngravingOne');
    const engTwo = document.getElementById('mejuriEngravingTwo');
    const cntTwo = document.getElementById('character_count_display_two');

    engOne?.removeAttribute('maxlength'); engOne && (engOne.value = '');
    engTwo?.removeAttribute('maxlength'); engTwo && (engTwo.value = '');
    document.getElementById('fontPreviewMejuriOne').textContent = '';
    document.getElementById('fontPreviewMejuriTwo').textContent = '';
    document.querySelectorAll('.errormsg').forEach(el => el.classList.add('is-hide'));

    const PLACEMENTS = {
      'rightvertical':     { one: 13 },
      'center-1line':      { one: 10 },
      'center-2lines':     { one: 7,  two: 10 },
      'center':            { one: 12, two: 12 },
      'bottomright-1line': { one: 4 },
      'bottomright-2lines':{ one: 2,  two: 4 },
    };

    const cfg  = PLACEMENTS[val] || { one: 10 };
    let   mxOne = cfg.one;
    const mxTwo = cfg.two;

    if (val === 'rightvertical' && isEngravingStyle) mxOne = 12;

    engOne?.setAttribute('maxlength', mxOne);
    engOne?.setAttribute('data-required', 'true');
    document.getElementById('character_count_display_one').textContent = `0/${mxOne}`;

    if (mxTwo) {
      engTwo?.setAttribute('maxlength', mxTwo);
      engTwo?.setAttribute('data-required', 'true');
      engTwo?.classList.remove('is-hide');
      engTwo && (engTwo.disabled = false);
      cntTwo?.classList.remove('is-hide');
      cntTwo && (cntTwo.textContent = `0/${mxTwo}`);
      document.getElementById('character_count_display_two').textContent = `0/${mxTwo}`;
    } else {
      engTwo?.classList.add('is-hide');
      engTwo && (engTwo.disabled = true);
      engTwo?.removeAttribute('data-required');
      cntTwo?.classList.add('is-hide');
    }

    engOne?.addEventListener('input', function () {
      document.getElementById('character_count_display_one').textContent = `${this.value.length}/${mxOne}`;
      document.getElementById('fontPreviewMejuriOne').textContent = this.value;
    });
    if (mxTwo) {
      engTwo?.addEventListener('input', function () {
        document.getElementById('character_count_display_two').textContent = `${this.value.length}/${mxTwo}`;
        document.getElementById('fontPreviewMejuriTwo').textContent = this.value;
      });
    }
  }

  function countEngraving(key) {
    const input = document.querySelector(`.flyout-engraving-text-one-${key}`);
    if (!input) return;
    const val = input.value || '';
    const max = input.getAttribute('maxlength') || '40';
    document.getElementById('character_count_display_one').textContent = `${val.length}/${max}`;
    document.getElementById('fontPreviewMejuriOne').textContent = val;
  }

  function countFont(key, charCount) {
    const input = document.querySelector(`.flyout-engraving-text-one-${key}`);
    if (!input) return;
    const val = input.value || '';
    const max = input.getAttribute('maxlength') || charCount;
    document.getElementById('character_count_display_one').textContent = `${val.length}/${max}`;
    document.getElementById('fontPreviewMejuriOne').textContent = val;
  }

  function setupFontPreview() {
    const sel    = document.querySelector('.custom-font-selector')?.value || '';
    const family = sel === 'Bernhard Tango' ? 'BernhardTangoBT' : sel === 'Celine' ? 'BV Celine' : sel.replace(/\s/g, '');
    const tcase  = sel === 'Celine' ? 'uppercase' : 'none';
    const one    = document.getElementById('fontPreviewMejuriOne');
    const two    = document.getElementById('fontPreviewMejuriTwo');
    if (one?.style) Object.assign(one.style, { fontFamily: family, textTransform: tcase });
    if (two?.style) Object.assign(two.style, { fontFamily: family, textTransform: tcase });
  }

  /* ======= Empty all ======= */
  function emptyAll() {
    selectedProducts  = [];
    checkedProducts   = [];
    variantPriceMap   = [];
    renderSelectedProducts();
    if (calcPriceEl) calcPriceEl.textContent = '';
    addBtn.textContent = 'ADD';
  }

  /* ======= Validate all flyout selections before ATC ======= */
  function validateFlyoutSelections() {
    if (!selectedProducts.length) return true;

    let valid = true;

    selectedProducts.forEach(product => {
      const key = product.sourceId;

      /* ── 1. Variant option dropdowns ── */
      const container = document.getElementById(`insert-dropdown-${key}`);
      if (container) {
        container.querySelectorAll('select[data-required="true"]').forEach((sel, idx) => {
          const errSpan = document.getElementById(`validation-opt-${idx + 1}-${key}`);
          if (!sel.value) {
            valid = false;
            if (errSpan) errSpan.style.display = 'block';
          } else {
            if (errSpan) errSpan.style.display = 'none';
          }
        });
      }
      /* ── 2. Engraving fields (only when toggle is ON) ── */
      const engToggle = document.getElementById(`engravablesInputsWrapperMejuri${key}`);
      if (!engToggle?.checked) return;

      // Scope all lookups to this product's card
      const card = engToggle.closest('.flyout-list');

      // Helper scoped to this card
      const showErr = (el, msg) => {
        if (!el) return;
        el.textContent = msg;
        el.classList.remove('is-hide');
      };
      const clearErr = (el) => {
        if (!el) return;
        el.textContent = '';
        el.classList.add('is-hide');
      };

      // Font selector
      const fontSel = card?.querySelector(`#selected-font-${key}`);
      const fontErr = card?.querySelector('.font-selector-flyout-error');
      if (fontSel && !fontSel.value) {
        valid = false;
        showErr(fontErr, 'Please select a font.');
      } else {
        clearErr(fontErr);
      }

      // Placement selector (only present on engraving-placement products)
      const placeSel = card?.querySelector(`#selected-placement-${key}`);
      const placeErr = card?.querySelector('.placement-selector-flyout-error');
      if (placeSel && !placeSel.value) {
        valid = false;
        showErr(placeErr, 'Please select a placement.');
      } else if (placeSel) {
        clearErr(placeErr);
      }

      // Engraving text line 1
      const textOne    = card?.querySelector(`.flyout-engraving-text-one-${key}`);
      const textOneErr = card?.querySelector('.custom-text-one-flyout-error');
      if (textOne && !textOne.value.trim()) {
        valid = false;
        showErr(textOneErr, 'Please enter engraving text.');
      } else {
        clearErr(textOneErr);
      }
    });

    return valid;
  }

  /* ======= Expose minimal public API ======= */
  window.__flyout = {
    open:              openDrawer,
    close:             closeDrawer,
    empty:             emptyAll,
    toggleEngraving,
    setMaxLength,
    countEngraving,
    countFont,
    setupFontPreview,
    setCalculatedPrice,
    getSelected:       () => selectedProducts,
    getVariantMap:     () => variantPriceMap,
    getStackVariants:  () => selectedStackVariantIds,
    validateFlyoutSelections
  };

  /* ======= Initialization ======= */
  buildDrawerList();

})();